import {
  Box,
  Button,
  Card,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import { Clock, ExternalLink, Info, RefreshCw } from 'react-feather';
import { Sparkles } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { aiApi } from '../../../../services/api/aiApi';
import type {
  ComposeEnvelope,
  PreSessionGuidanceOutput,
  PreSessionGuidanceSourceType,
} from '../../../../services/api/aiTypes';
import { SourceChip } from './SourceChip';

// MED-1100 — tokens taken directly from the Figma frame (node 1993:18080).
const PANEL_BORDER = '#e5e7eb';
const TITLE_NAVY = '#1a1a2e';
const GENERATED_PURPLE = '#7c3aed';
const LABEL_GRAY = '#9ca3af';
const SENTIMENT_BG = '#203358';
const TONE_BG = '#ebf2fa';
const TONE_BORDER = '#b0cce0';
const TONE_FG = '#203358';
const THEME_BG = '#f3f4f6';
const THEME_BORDER = '#e5e7eb';
const THEME_FG = '#374151';
const GUIDANCE_FG = '#374151';
const NUM_BADGE_BG = '#ebf2fa';
const NUM_BADGE_FG = '#3b82b0';
const UPDATED_FG = '#6b7280';
const FOOTER_BG = '#fffdf6';
const FOOTER_BORDER = '#f3f4f6';
const FOOTER_BTN_BORDER = '#e0ded9';

// MED-1100 AC6 — the footer ("Refresh your memory from Session N: {title} →" +
// "View full report") is built to the Figma but kept hidden: it has no backing
// data (the guidance output carries no prior-session field) and no nav
// destination yet. Flip to true once those are defined (see the MED-1100
// comment) and wire the real prior-session label + onViewReport handler.
const SHOW_FOOTER = false;

/**
 * MED-909 AC3 — peer leaders facilitate coworkers, so source detail labels must
 * never name individual group members. When `anonymizeSources` is on we replace
 * the (potentially name-bearing) detail with a generic type label. Methodology
 * references (`medley_guidance`, e.g. "Holding Space §1.2") carry no PII, so
 * they pass through unchanged.
 */
const ANONYMIZED_SOURCE_LABELS: Partial<
  Record<PreSessionGuidanceSourceType, string>
> = {
  reflection: 'Participant pre-work',
  past_session: 'Past session',
  attendance_pattern: 'Attendance pattern',
  // MED-1208 (D14) — intake-grounded guidance; generic label so no goal text
  // or count phrasing from the model reaches a peer view unvetted.
  matching_form: 'Matching form',
};

function anonymizedDetail(
  type: PreSessionGuidanceSourceType,
  detail: string
): string {
  return ANONYMIZED_SOURCE_LABELS[type] ?? detail;
}

/** MED-1100 — section labels (Overall Sentiment / Emotional Tone / Key Themes). */
function SectionLabel({ children }: { children: string }) {
  return (
    <Text
      fz={12}
      fw={600}
      c={LABEL_GRAY}
      tt="uppercase"
      style={{ letterSpacing: 0.72, lineHeight: 1.55 }}
    >
      {children}
    </Text>
  );
}

/**
 * MED-876 AC5 — Sentiment & Guidance panel per group tab.
 *
 * Visual reference: Figma node 1993:18080 (MED-1100).
 *
 * Per IV5, the fetch is non-blocking — the panel renders its own skeleton +
 * empty state without blocking the rest of the page. Per AC5 fail_open path,
 * the empty state offers a "write your own" affordance via Leader Notes.
 */
export interface SentimentGuidancePanelProps {
  sessionId: string;
  groupId: string;
  groupName: string;
  /**
   * MED-909 AC3 — when true, source detail chips are anonymized so no individual
   * group member is named. Peer-leader prep passes true; coach prep omits it.
   */
  anonymizeSources?: boolean;
  /**
   * MED-1100 — DEV preview only: a pre-seeded guidance envelope. When set, the
   * panel renders it directly instead of calling the AI backend (the dev preview
   * has no `POST /api/ai/pre-session-guidance`). Undefined in production.
   */
  previewEnvelope?: ComposeEnvelope<PreSessionGuidanceOutput>;
}

export function SentimentGuidancePanel({
  sessionId,
  groupId,
  groupName,
  anonymizeSources = false,
  previewEnvelope,
}: SentimentGuidancePanelProps) {
  const [envelope, setEnvelope] =
    useState<ComposeEnvelope<PreSessionGuidanceOutput> | null>(
      previewEnvelope ?? null
    );
  const [loading, setLoading] = useState(!previewEnvelope);

  const fetchGuidance = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const env = await aiApi.preSessionGuidance({
        session_id: sessionId,
        group_id: groupId,
      });
      setEnvelope(env);
    } finally {
      setLoading(false);
    }
  }, [sessionId, groupId]);

  useEffect(() => {
    if (!previewEnvelope) void fetchGuidance();
  }, [fetchGuidance, previewEnvelope]);

  const isFailOpen =
    envelope?.status === 'fail_open' ||
    (envelope !== null && envelope.result === null);
  const result = envelope?.result ?? null;
  const updatedAtLabel = envelope?.metadata.generated_at
    ? formatRelative(envelope.metadata.generated_at)
    : '';

  const guidance = result?.session_leader_guidance ?? [];

  return (
    <Card withBorder padding={0} radius={16} style={{ overflow: 'hidden' }}>
      {/* Header */}
      <Box
        style={{
          padding: '24px 28px 19px',
          borderBottom: `1px solid ${PANEL_BORDER}`,
        }}
      >
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={3}>
            <Group gap={4} align="center" wrap="nowrap">
              <Sparkles size={12} color={GENERATED_PURPLE} />
              <Text fz={11.8} c={GENERATED_PURPLE} style={{ lineHeight: 1.55 }}>
                Generated from participant pre-work responses — refreshes as new
                submissions arrive
              </Text>
            </Group>
            <Text fw={600} fz={17} c={TITLE_NAVY} style={{ lineHeight: 1.5 }}>
              {groupName} Sentiment &amp; Guidance
            </Text>
          </Stack>
          {updatedAtLabel && (
            <Group
              gap={6}
              align="center"
              wrap="nowrap"
              style={{ flexShrink: 0 }}
            >
              <Clock size={15} color={UPDATED_FG} />
              <Text fz={13.5} c={UPDATED_FG} style={{ whiteSpace: 'nowrap' }}>
                {updatedAtLabel}
              </Text>
            </Group>
          )}
        </Group>
      </Box>

      {loading && (
        <Group justify="center" py="xl">
          <Loader size="sm" />
          <Text size="sm" c="dimmed">
            Loading sentiment…
          </Text>
        </Group>
      )}

      {!loading && isFailOpen && (
        <Stack gap="xs" p={28}>
          <Group gap="xs" align="center">
            <ThemeIcon variant="light" color="gray" size="md" radius="xl">
              <Info size={14} />
            </ThemeIcon>
            <Text size="sm" fw={600}>
              AI sentiment unavailable
            </Text>
          </Group>
          <Text size="sm" c="dimmed">
            You can still review pre-work responses and add your own notes in
            the agenda above — the coach is never blocked.
          </Text>
          <Group>
            <Button
              size="xs"
              variant="subtle"
              leftSection={<RefreshCw size={12} />}
              onClick={() => void fetchGuidance()}
            >
              Retry
            </Button>
          </Group>
        </Stack>
      )}

      {!loading && !isFailOpen && result && (
        <>
          {/* Sentiment / tone / themes */}
          <Box
            style={{
              padding: '20px 28px',
              borderBottom: `1px solid ${PANEL_BORDER}`,
            }}
          >
            <Stack gap={14}>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing={24}>
                <Stack gap={8} align="flex-start">
                  <SectionLabel>Overall Sentiment</SectionLabel>
                  <Box
                    style={{
                      backgroundColor: SENTIMENT_BG,
                      borderRadius: 20,
                      padding: '5px 16px',
                      alignSelf: 'flex-start',
                    }}
                  >
                    <Text
                      fz={13}
                      fw={600}
                      c="#fff"
                      style={{ lineHeight: 1.55 }}
                    >
                      {result.overall_sentiment.label}
                    </Text>
                  </Box>
                </Stack>
                <Stack gap={8} align="flex-start">
                  <SectionLabel>Emotional Tone</SectionLabel>
                  <Box
                    style={{
                      backgroundColor: TONE_BG,
                      border: `1px solid ${TONE_BORDER}`,
                      borderRadius: 20,
                      padding: '6px 17px',
                      alignSelf: 'flex-start',
                    }}
                  >
                    <Text
                      fz={13}
                      fw={600}
                      c={TONE_FG}
                      style={{ lineHeight: 1.55 }}
                    >
                      {result.emotional_tone.label}
                    </Text>
                  </Box>
                </Stack>
              </SimpleGrid>

              <Stack gap={8}>
                <SectionLabel>Key Themes</SectionLabel>
                <Group gap={12} wrap="wrap">
                  {result.key_themes.map((kt, idx) => (
                    <Box
                      key={idx}
                      style={{
                        backgroundColor: THEME_BG,
                        border: `1px solid ${THEME_BORDER}`,
                        borderRadius: 20,
                        padding: '5px 15px',
                      }}
                    >
                      <Text fz={13} c={THEME_FG} style={{ lineHeight: 1.55 }}>
                        {kt.theme}
                      </Text>
                    </Box>
                  ))}
                </Group>
              </Stack>
            </Stack>
          </Box>

          {/* Session Leader Guidance */}
          <Box style={{ padding: '19px 28px' }}>
            <Stack gap={3}>
              <Text
                fw={600}
                fz={16}
                c={TITLE_NAVY}
                style={{ lineHeight: 1.55 }}
              >
                Session Leader Guidance
              </Text>
              <Text fz={13} c={LABEL_GRAY} style={{ lineHeight: 1.55 }}>
                Key considerations for {groupName}
              </Text>
            </Stack>
            <Stack gap={0} mt={4}>
              {guidance.map((item, idx) => (
                <Box
                  key={item.number}
                  style={{
                    padding: '20px 0',
                    borderBottom:
                      idx < guidance.length - 1
                        ? `1px solid ${PANEL_BORDER}`
                        : undefined,
                  }}
                >
                  <Group gap={14} align="flex-start" wrap="nowrap">
                    <Box
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        backgroundColor: NUM_BADGE_BG,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Text
                        fz={11}
                        fw={700}
                        c={NUM_BADGE_FG}
                        style={{ lineHeight: 1 }}
                      >
                        {item.number}
                      </Text>
                    </Box>
                    <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
                      <Text
                        fz={14}
                        c={GUIDANCE_FG}
                        style={{ lineHeight: 1.65 }}
                      >
                        {item.guidance}
                      </Text>
                      <Group gap={4}>
                        <SourceChip
                          type={item.source.type}
                          detail={
                            anonymizeSources
                              ? anonymizedDetail(
                                  item.source.type,
                                  item.source.detail
                                )
                              : item.source.detail
                          }
                        />
                      </Group>
                    </Stack>
                  </Group>
                </Box>
              ))}
            </Stack>
          </Box>

          {/* MED-1100 AC6 — footer (hidden via SHOW_FOOTER until data + nav
              exist; built to the Figma so it's ready to wire). */}
          {SHOW_FOOTER && (
            <Group
              justify="space-between"
              align="center"
              wrap="nowrap"
              style={{
                backgroundColor: FOOTER_BG,
                borderTop: `1px solid ${FOOTER_BORDER}`,
                padding: '13px 20px 12px',
              }}
            >
              {/* TODO(MED-1100): real prior-session number + title + link target. */}
              <Group gap={8} align="center" wrap="nowrap">
                <ExternalLink size={16} color={GUIDANCE_FG} />
                <Text fz={13} fw={500} c={GUIDANCE_FG}>
                  Refresh your memory from the previous session →
                </Text>
              </Group>
              {/* TODO(MED-1100): wire onViewReport to the full-report destination. */}
              <Button
                variant="default"
                radius={6}
                size="xs"
                styles={{
                  root: { borderColor: FOOTER_BTN_BORDER },
                  label: { color: '#1a1a1a', fontSize: 12, fontWeight: 600 },
                }}
              >
                View full report
              </Button>
            </Group>
          )}
        </>
      )}
    </Card>
  );
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const seconds = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (seconds < 60) return 'Updated just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60)
    return `Updated ${minutes} min${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Updated ${hours} hr${hours === 1 ? '' : 's'} ago`;
  return `Updated ${new Date(iso).toLocaleDateString()}`;
}

export default SentimentGuidancePanel;
